// src/pages/ProductDetails.jsx - FULL CODE with PDF URL + Base64 Support + Family Premium + Single Payment Fix + Dependent Spinner
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Swal from 'sweetalert2';
// Components
import TopHeader from '../components/dashboard/TopHeader/TopHeader';
import Sidebar from '../components/Global/sidebar'; // ✅ Import Reusable Sidebar
import { authenticatedFetch, clearStoredCredentials, getAuthHeaders, getStoredCredentials } from '../utils/api';
// Styles
import '../styles/productDetails.css';

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');
const DEFAULT_MINIMUM_PREMIUM = 1100;
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_xxxxxxxxxxxxxxxx';

// HMO Department configuration
const HMO_DEPARTMENTS = {
  'Medical': { color: 'danger', icon: 'fa-stethoscope' },
  'Nursing': { color: 'info', icon: 'fa-user-nurse' },
  'Administration': { color: 'primary', icon: 'fa-building' },
  'Pharmacy': { color: 'success', icon: 'fa-pills' },
  'Laboratory': { color: 'warning', icon: 'fa-flask' },
  'Records': { color: 'secondary', icon: 'fa-folder-open' },
  'Billing': { color: 'purple', icon: 'fa-file-invoice' },
  'Customer Care': { color: 'info', icon: 'fa-headset' },
};

// Payment periods (1-12 months)
const PAYMENT_PERIODS = Array.from({ length: 12 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1} Month${i + 1 > 1 ? 's' : ''}`,
  months: i + 1
}));

// Relationship options for dependents
const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' }
];

// MAX DEPENDENTS LIMIT
const MAX_DEPENDENTS = 3;

// ============================================================================
// UTILITY FUNCTIONS (including FIXED PDF helpers)
// ============================================================================
const formatCurrency = (amount, currency = 'NGN') => {
  const num = parseFloat(amount) || 0;
  const symbols = { NGN: '₦', USD: '$', EUR: '€', GBP: '£' };
  return `${symbols[currency] || '₦'}${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getStatusColor = (status) => {
  const colors = {
    active: 'success', inactive: 'secondary', suspended: 'warning',
    terminated: 'danger', onLeave: 'info', expired: 'danger'
  };
  return colors[status?.toLowerCase()] || 'secondary';
};

const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const defaultOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-NG', { ...defaultOptions, ...options });
  } catch (e) {
    console.warn('Date format error:', dateString, e);
    return 'Invalid Date';
  }
};

// Calculate coverage status with countdown
const calculateCoverageStatus = (coverageEndDate, coverageDays) => {
  if (!coverageEndDate) return { remainingDays: null, isExpired: false, isExpiringSoon: false, status: 'unknown', countdown: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(coverageEndDate);
  endDate.setHours(23, 59, 59, 999);
  const diffTime = endDate - today;
  const remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const isExpired = remainingDays < 0 || (coverageDays !== undefined && coverageDays <= 0);
  const isExpiringSoon = !isExpired && remainingDays <= 30 && remainingDays >= 0;
  let countdown = null;
  if (!isExpired && remainingDays >= 0) {
    if (remainingDays > 0) {
      countdown = `${remainingDays}d ${new Date(diffTime).getUTCHours()}h ${new Date(diffTime).getUTCMinutes()}m`;
    } else {
      countdown = 'Expires today';
    }
  }
  const status = isExpired ? 'expired' : isExpiringSoon ? 'expiring' : 'active';
  return { remainingDays, isExpired, isExpiringSoon, status, countdown };
};

const generateReference = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let ref = 'HMO-';
  for (let i = 0; i < 10; i++) ref += chars.charAt(Math.floor(Math.random() * chars.length));
  return ref;
};

const getUserSession = () => {
  try { return JSON.parse(localStorage.getItem('userSession') || '{}'); }
  catch { return {}; }
};

// Convert file to base64 for API upload
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// ============================================================================
// ✅ FIXED PDF HELPER FUNCTIONS - Support URL + Base64 + Filename
// ============================================================================
const convertBase64ToBlob = (base64Data, contentType = 'application/pdf') => {
  const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');
  const binary = atob(base64);
  const len = binary.length;
  const buffer = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return new Blob([buffer], { type: contentType });
};

const downloadPDF = async (policyDocument, fileName = 'policy-document.pdf') => {
  if (!policyDocument) {
    toast.error('No policy document available to download');
    return;
  }
  try {
    let blob = null;
    let finalFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    if (policyDocument.startsWith('data:application/pdf;base64,') ||
      (/^[A-Za-z0-9+/=]+$/.test(policyDocument) && policyDocument.length > 100)) {
      const base64 = policyDocument.startsWith('data:')
        ? policyDocument
        : `application/pdf;base64,${policyDocument}`;
      blob = convertBase64ToBlob(base64);
    } else {
      const fileUrl = policyDocument.startsWith('http')
        ? policyDocument
        : `${API_BASE.replace(/\/api$/, '')}/policy-documents/${policyDocument}`;
      const authHeaders = getAuthHeaders();
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: authHeaders || {}
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error('❌ PDF fetch failed:', response.status, errorText);
        if (response.status === 401 || errorText.includes('Authorization')) {
          toast.error('Authentication required. Please log in again.');
          return;
        }
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition?.includes('filename=')) {
        const match = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
        if (match?.[1]) finalFileName = match[1];
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Download started');
  } catch (error) {
    console.error('❌ PDF download error:', error);
    toast.error(error.message || 'Failed to download policy document');
  }
};

const viewPDF = async (policyDocument) => {
  if (!policyDocument) {
    toast.error('No policy document available to view');
    return;
  }
  try {
    let url = null;
    if (policyDocument.startsWith('data:application/pdf;base64,') ||
      (/^[A-Za-z0-9+/=]+$/.test(policyDocument) && policyDocument.length > 100)) {
      const base64 = policyDocument.startsWith('data:')
        ? policyDocument
        : `application/pdf;base64,${policyDocument}`;
      const blob = convertBase64ToBlob(base64);
      url = URL.createObjectURL(blob);
    } else {
      const fileUrl = policyDocument.startsWith('http')
        ? policyDocument
        : `${API_BASE.replace(/\/api$/, '')}/policy-documents/${policyDocument}`;
      const authHeaders = getAuthHeaders();
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: authHeaders || {}
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        if (response.status === 401 || errorText.includes('Authorization')) {
          toast.error('Authentication required. Please log in again.');
          return;
        }
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      const blob = await response.blob();
      url = URL.createObjectURL(blob);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (error) {
    console.error('❌ PDF view error:', error);
    toast.error(error.message || 'Failed to open policy document');
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const staffPhotoInputRef = useRef(null);

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [activeDetailsTab, setActiveDetailsTab] = useState('benefits');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data State
  const [product, setProduct] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  // Filter State
  const [staffSearch, setStaffSearch] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Modal State
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBulkRenewalModal, setShowBulkRenewalModal] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  // DEPENDENTS MODAL STATE - MAX 3
  const [showDependentsModal, setShowDependentsModal] = useState(false);
  const [dependentsList, setDependentsList] = useState([]);
  const [newDependent, setNewDependent] = useState({
    firstName: '', lastName: '', relationship: '', age: '', gender: '',
    phone: '', email: '', dateOfBirth: '', photo: null
  });
  const [editingDependentId, setEditingDependentId] = useState(null);
  const [dependentsLoading, setDependentsLoading] = useState(false);
  // ✅ NEW: State for Dependent Saving Spinner
  const [isSavingDependent, setIsSavingDependent] = useState(false);

  // Form State
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', gender: '', department: '', productId: id,
    nin: '', homeAddress: '', profilePhoto: null
  });
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [pendingStaffData, setPendingStaffData] = useState(null);

  // Payment State
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentReference, setPaymentReference] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // ✅ NEW: Premium Type Selection State
  const [selectedPremiumType, setSelectedPremiumType] = useState('minimum'); // 'minimum' or 'family'

  // Summary State
  const [staffSummary, setStaffSummary] = useState({
    totalStaff: 0, activeStaff: 0, totalPremium: 0, byDepartment: {}, expiredCount: 0, expiringSoonCount: 0
  });

  // Derived values
  const getMinimumPremium = useCallback(() => product?.minimumPremiumValue || DEFAULT_MINIMUM_PREMIUM, [product]);
  const premiumPerStaff = useMemo(() => getMinimumPremium(), [getMinimumPremium]);
  
  // ✅ NEW: Dynamic Active Premium based on selection
  const activePremium = useMemo(() => {
    if (selectedPremiumType === 'family') {
      return product?.familyPremiumValue || 0;
    }
    return product?.minimumPremiumValue || DEFAULT_MINIMUM_PREMIUM;
  }, [product, selectedPremiumType]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    if (window.PaystackPop) {
      setPaystackLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => {
      console.log('✅ Paystack SDK loaded');
      setPaystackLoaded(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Paystack SDK');
      toast.error('Failed to load payment gateway. Check your internet connection.');
    };
    document.body.appendChild(script);
    return () => {
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    if (id) fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === 'list_of_staff' && product?.id) {
      fetchProductStaff();
    }
  }, [activeTab, product?.id]);

  useEffect(() => {
    if (product && staffList.length >= 0) {
      calculateStaffSummary(staffList);
    }
  }, [product, staffList]);

  useEffect(() => {
    if (activeTab !== 'list_of_staff' || staffList.length === 0) return;
    countdownIntervalRef.current = setInterval(() => {
      setStaffList(prev => [...prev]);
    }, 60000);
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [activeTab, staffList.length]);

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(`${API_BASE}/products/products`, 'POST', {});
      const isSuccess = response?.success === true || response?.status === 'success';
      if (isSuccess) {
        const allProducts = response.data || response.result || [];
        const found = allProducts.find(p => p.id === Number(id) || p.Id === Number(id));
        if (found) {
          const formatted = {
            id: found.id || found.Id,
            name: found.name || found.Name,
            category: found.categoryName || found.CategoryName || 'Uncategorized',
            provider: found.providerName || found.ProviderName || 'Unknown',
            minimumPremium: `₦${(found.minimumPremium || found.MinimumPremium || DEFAULT_MINIMUM_PREMIUM).toLocaleString()}`,
            minimumPremiumValue: found.minimumPremium || found.MinimumPremium || DEFAULT_MINIMUM_PREMIUM,
            familyPremium: `₦${(found.familyPremium || found.FamilyPremium || 0).toLocaleString()}`,
            familyPremiumValue: found.familyPremium || found.FamilyPremium || 0,
            status: found.status || found.Status || 'Inactive',
            grossAmount: '₦0', totalCommissions: '₦0', totalSales: 0, totalCustomers: 0,
            policyDocument: found.policyDocument || found.PolicyDocument || null,
            benefits: found.benefits || found.Benefits || 'No benefits information available.',
            howItWorks: found.howItWorks || found.HowItWorks || 'No information available.',
            howToClaim: found.howToClaim || found.HowToClaim || 'No information available.',
            hospitals: Array.isArray(found.hospitals) ? found.hospitals : [],
            raw: found
          };
          setProduct(formatted);
          if (activeTab === 'list_of_staff') fetchProductStaff();
        } else {
          setError('Product not found');
          toast.error('Product not found');
        }
      } else {
        handleAuthError(response);
        throw new Error(response?.message || 'Failed to fetch product');
      }
    } catch (err) {
      console.error('💥 Product fetch error:', err);
      handleAuthError(err);
      setError(err.message || 'Could not load product details');
      toast.error(err.message || 'Could not load product details');
    } finally {
      setLoading(false);
    }
  }, [id, activeTab]);

  const fetchProductStaff = async () => {
    if (!product?.id) return;
    try {
      setStaffLoading(true);
      const session = getUserSession();
      const result = await authenticatedFetch(
        `${API_BASE}/Settings/get-staff-by-product`,
        'POST',
        { productId: parseInt(product.id), companyId: session.companyId }
      );
      if (result?.success === true && result?.data?.staff) {
        const staffList = result.data.staff.map(staff => ({
          staffId: staff.staffId,
          productId: staff.productId,
          companyId: staff.companyId,
          firstName: staff.firstName,
          lastName: staff.lastName,
          email: staff.email,
          phone: staff.phone,
          gender: staff.gender,
          department: staff.department,
          fullName: staff.fullName,
          nin: staff.nin,
          homeAddress: staff.homeAddress,
          profilePhoto: staff.profilePhoto,
          coverageStartDate: staff.coverageStartDate,
          coverageEndDate: staff.coverageEndDate,
          coverageDays: staff.coverageDays || 0,
          periodMonths: staff.periodMonths,
          premiumPerStaff: staff.premiumPerStaff,
          totalPremium: staff.totalPremium,
          paymentReference: staff.paymentReference,
          paymentStatus: staff.paymentStatus,
          status: staff.currentStatus,
          isActive: staff.isActive,
          createdAt: staff.createdAt,
          profileImageUrl: null
        }));
        setStaffList(staffList);
        if (result.data.summary) {
          setStaffSummary({
            totalStaff: result.data.summary.totalStaff,
            activeStaff: result.data.summary.activeStaff,
            totalPremium: result.data.summary.totalPremium,
            byDepartment: result.data.summary.byDepartment?.reduce((acc, dept) => {
              acc[dept.department] = { count: dept.count, premium: dept.totalPremium, active: dept.active };
              return acc;
            }, {}) || {},
            expiredCount: result.data.summary.expiredCount || 0,
            expiringSoonCount: result.data.summary.expiringSoonCount || 0
          });
        }
      } else {
        setStaffList([]);
        calculateStaffSummary([]);
        if (result?.message) {
          if (result.message.includes('Invalid or expired session')) {
            handleSessionExpired();
          } else {
            toast.warning(result.message);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch HMO staff:', error);
      setStaffList([]);
      calculateStaffSummary([]);
      handleAuthError(error);
      toast.error('Failed to load staff list. Please try again.');
    } finally {
      setStaffLoading(false);
    }
  };

  // ============================================================================
  // BUSINESS LOGIC
  // ============================================================================
  const handleAuthError = (error) => {
    const errorMsg = error?.message?.toLowerCase() || (typeof error === 'string' ? error.toLowerCase() : '');
    if (errorMsg.includes('401') || errorMsg.includes('expired') || errorMsg.includes('unauthorized') || errorMsg.includes('authorization')) {
      clearStoredCredentials();
      localStorage.removeItem('userSession');
      toast.warning('Session expired. Please log in again.');
      navigate('/login', { replace: true });
      return true;
    }
    return false;
  };

  const handleSessionExpired = () => {
    toast.warning('Session expired. Please log in again.');
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    navigate('/login', { replace: true });
  };

  const calculateStaffSummary = useCallback((staffList) => {
    const premium = getMinimumPremium(); // Keep summary based on minimum premium
    const totalStaff = staffList.length;
    const activeStaff = staffList.filter(s => s.status === 'active').length;
    const totalPremium = totalStaff * premium;
    let expiredCount = 0;
    let expiringSoonCount = 0;
    const byDepartment = {};
    staffList.forEach(staff => {
      const dept = staff.department || 'Unassigned';
      if (!byDepartment[dept]) byDepartment[dept] = { count: 0, premium: 0, active: 0 };
      byDepartment[dept].count++;
      byDepartment[dept].premium += premium;
      if (staff.status === 'active') byDepartment[dept].active++;
      const { isExpired, isExpiringSoon } = calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays);
      if (isExpired) expiredCount++;
      else if (isExpiringSoon) expiringSoonCount++;
    });
    setStaffSummary({ totalStaff, activeStaff, totalPremium, byDepartment, expiredCount, expiringSoonCount });
  }, [getMinimumPremium]);

  const handlePeriodSelect = (period) => {
    if (loading || staffLoading) return;
    setSelectedPeriod(period);
    const calculatedPremium = activePremium * period.months; // ✅ Use activePremium
    setPaymentAmount(calculatedPremium);
    setShowPeriodDropdown(false);
  };

  const resetStaffForm = () => {
    setStaffForm({
      firstName: '', lastName: '', email: '', phone: '', gender: '', department: '', productId: id,
      nin: '', homeAddress: '', profilePhoto: null
    });
    setEditingStaff(null);
    setSelectedPeriod(null);
    setPaymentAmount(0);
    setSelectedPremiumType('minimum'); // ✅ Reset premium type
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (staffPhotoInputRef.current) staffPhotoInputRef.current.value = '';
  };

  const fetchDependentsFromBackend = async (staffId, companyId) => {
    if (!staffId || !companyId) return [];
    setDependentsLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/get-dependents-by-staff`,
        'POST',
        { StaffId: parseInt(staffId), CompanyId: parseInt(companyId) }
      );
      if (response?.status === 'success') {
        const mappedDependents = response.data.map(dep => ({
          id: dep.Id,
          staffId: dep.StaffId,
          companyId: dep.CompanyId,
          firstName: dep.FirstName,
          lastName: dep.LastName,
          relationship: dep.Relationship,
          age: dep.Age,
          gender: dep.Gender,
          phone: dep.Phone,
          email: dep.Email,
          dateOfBirth: dep.DateOfBirth,
          photo: dep.Photo,
          createdDate: dep.CreatedDate,
          modifiedDate: dep.ModifiedDate
        }));
        setDependentsList(mappedDependents);
        return mappedDependents;
      } else {
        toast.error(response?.message || 'Failed to fetch dependents');
        return [];
      }
    } catch (error) {
      console.error('💥 Fetch dependents error:', error);
      handleAuthError(error);
      toast.error('Could not load dependents from server');
      return [];
    } finally {
      setDependentsLoading(false);
    }
  };

  const openDependentsModal = () => {
    if (dependentsList.length >= MAX_DEPENDENTS) {
      toast.warning(`Maximum ${MAX_DEPENDENTS} dependents allowed`);
      return;
    }
    setShowDependentsModal(true);
    setEditingDependentId(null);
    setNewDependent({
      firstName: '', lastName: '', relationship: '', age: '', gender: '',
      phone: '', email: '', dateOfBirth: '', photo: null
    });
  };

  const closeDependentsModal = () => {
    setShowDependentsModal(false);
    setEditingDependentId(null);
    setNewDependent({
      firstName: '', lastName: '', relationship: '', age: '', gender: '',
      phone: '', email: '', dateOfBirth: '', photo: null
    });
  };

  const openStaffDependentsModal = async (staff) => {
    const session = getUserSession();
    if (!staff?.staffId || !session?.companyId) {
      toast.error('Unable to load dependents: Missing staff or company information');
      return;
    }
    setEditingStaff(staff);
    await fetchDependentsFromBackend(staff.staffId, session.companyId);
    setShowDependentsModal(true);
    setEditingDependentId(null);
    setNewDependent({
      firstName: '', lastName: '', relationship: '', age: '', gender: '',
      phone: '', email: '', dateOfBirth: '', photo: null
    });
  };

  // ✅ UPDATED: Added Spinner Logic & Enhanced Success Message
  const handleAddDependent = async (e) => {
    e.preventDefault();
    if (!newDependent.firstName || !newDependent.lastName || !newDependent.relationship) {
      toast.error('First name, last name, and relationship are required');
      return;
    }
    if (dependentsList.length >= MAX_DEPENDENTS && !editingDependentId) {
      toast.warning(`Maximum ${MAX_DEPENDENTS} dependents allowed`);
      return;
    }
    const session = getUserSession();
    const staffId = editingStaff?.staffId;
    const companyId = session?.companyId;
    if (!staffId || !companyId) {
      toast.error('Unable to save dependent: Missing staff or company information');
      return;
    }
    
    setIsSavingDependent(true); // Start spinner
    try {
      const payload = {
        StaffId: parseInt(staffId),
        CompanyId: parseInt(companyId),
        FirstName: newDependent.firstName,
        LastName: newDependent.lastName,
        Relationship: newDependent.relationship,
        Age: newDependent.age ? parseInt(newDependent.age) : null,
        Gender: newDependent.gender || null,
        Phone: newDependent.phone || null,
        Email: newDependent.email || null,
        DateOfBirth: newDependent.dateOfBirth || null,
        Photo: newDependent.photo || null
      };
      const endpoint = editingDependentId
        ? `${API_BASE}/Settings/update-dependent`
        : `${API_BASE}/Settings/save-dependent`;
      const response = await authenticatedFetch(endpoint, 'POST', payload);
      if (response?.status === 'success') {
        // ✅ Enhanced Success Message
        toast.success(editingDependentId ? '✅ Dependent updated successfully!' : '✅ Dependent added successfully!');
        if (staffId && companyId) {
          await fetchDependentsFromBackend(staffId, companyId);
        }
        setNewDependent({
          firstName: '', lastName: '', relationship: '', age: '', gender: '',
          phone: '', email: '', dateOfBirth: '', photo: null
        });
        setEditingDependentId(null);
      } else {
        throw new Error(response?.message || 'Failed to save dependent');
      }
    } catch (error) {
      console.error('💥 Save dependent error:', error);
      handleAuthError(error);
      toast.error(error.message || 'Failed to save dependent');
    } finally {
      setIsSavingDependent(false); // Stop spinner
    }
  };

  const handleEditDependent = (dependent) => {
    setNewDependent(dependent);
    setEditingDependentId(dependent.id);
  };

  const handleDeleteDependent = async (id) => {
    const result = await Swal.fire({
      title: 'Remove Dependent?',
      text: 'Are you sure you want to remove this dependent? This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    });
    if (result.isConfirmed) {
      try {
        const session = getUserSession();
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/delete-dependent`,
          'POST',
          { DependentId: id, CompanyId: session.companyId }
        );
        if (response?.status === 'success') {
          setDependentsList(prev => prev.filter(d => d.id !== id));
          toast.success('✅ Dependent removed successfully!');
        } else {
          throw new Error(response?.message || 'Delete failed');
        }
      } catch (error) {
        console.error('Delete dependent error:', error);
        toast.error(error.message || 'Failed to delete dependent');
      }
    }
  };

  const handleDependentChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo' && files?.[0]) {
      fileToBase64(files[0]).then(base64 => {
        setNewDependent(prev => ({ ...prev, photo: base64 }));
      });
    } else {
      setNewDependent(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleStaffPhotoUploadInTable = async (e, staffId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG or PNG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    try {
      const loadingToast = toast.loading('Uploading photo...');
      const base64 = await fileToBase64(file);
      const session = getUserSession();
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/update-staff-photo`,
        'POST',
        {
          StaffId: parseInt(staffId),
          CompanyId: session.companyId,
          ProfilePhoto: base64
        }
      );
      toast.dismiss(loadingToast);
      if (response?.success === true) {
        toast.success('Photo updated successfully');
        await fetchProductStaff();
      } else {
        toast.error(response?.message || 'Failed to update photo');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    }
    e.target.value = '';
  };

  const handleStaffPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG or PNG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setStaffForm(prev => ({ ...prev, profilePhoto: base64 }));
      toast.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    }
  };

  const openAddStaffModal = () => {
    resetStaffForm();
    setDependentsList([]);
    setPendingStaffData({
      firstName: '', lastName: '', email: '', phone: '',
      gender: '', department: '', productId: id,
      nin: '', homeAddress: '', profilePhoto: null
    });
    setShowAddStaffModal(true);
  };

  const openEditStaffModal = async (staff) => {
    setStaffForm({
      firstName: staff.firstName || '',
      lastName: staff.lastName || '',
      email: staff.email || '',
      phone: staff.phone || '',
      gender: staff.gender || '',
      department: staff.department || '',
      productId: id,
      nin: staff.nin || '',
      homeAddress: staff.homeAddress || '',
      profilePhoto: staff.profilePhoto || null
    });
    setEditingStaff(staff);
    const session = getUserSession();
    if (staff.staffId && session.companyId) {
      await fetchDependentsFromBackend(staff.staffId, session.companyId);
    } else {
      setDependentsList([]);
    }
    setShowAddStaffModal(true);
  };

  const closeStaffModal = () => {
    setShowAddStaffModal(false);
    setShowImportModal(false);
    setShowPaymentModal(false);
    setShowBulkRenewalModal(false);
    resetStaffForm();
    setImportFile(null);
    setImportPreview([]);
    setPendingStaffData(null);
    setSelectedPeriod(null);
    setDependentsList([]);
    setSelectedPremiumType('minimum'); // ✅ Reset premium type
  };

  // ============================================================================
  // PAYMENT HANDLERS
  // ============================================================================
  const preparePremiumPayment = async (isImport = false, importCount = 1) => {
    if (!paystackLoaded) {
      toast.warning('Payment gateway is loading. Please wait...');
      return false;
    }
    if (!isImport) {
      if (!staffForm.firstName || !staffForm.lastName || !staffForm.email || !staffForm.department) {
        toast.error('First name, last name, email, and department are required');
        return false;
      }
      if (!selectedPeriod) {
        toast.error('Please select a period of cover');
        return false;
      }
    } else {
      if (!importFile) {
        toast.error('Please select a file first');
        return false;
      }
      if (!selectedPeriod) {
        toast.error('Please select a coverage period');
        return false;
      }
      if (!pendingStaffData?.staffCount || pendingStaffData.staffCount <= 0) {
        toast.error('No valid staff records found');
        return false;
      }
    }

    // ✅ Use activePremium for calculation
    const calculatedAmount = isImport
      ? (pendingStaffData.staffCount * activePremium * selectedPeriod.months)
      : (activePremium * selectedPeriod.months);

    const reference = generateReference();
    setPaymentAmount(calculatedAmount);
    setPaymentReference(reference);
    
    setPendingStaffData(prev => ({
      ...prev,
      paymentReference: reference,
      premiumAmount: calculatedAmount,
      periodMonths: selectedPeriod.months,
      periodLabel: selectedPeriod.label,
      isImport,
      importCount: isImport ? pendingStaffData?.staffCount : 1,
      // ✅ FIX: Explicitly map staffForm fields for single enrollment to prevent NULL errors
      firstName: isImport ? null : staffForm.firstName,
      lastName: isImport ? null : staffForm.lastName,
      email: isImport ? null : staffForm.email,
      phone: isImport ? null : staffForm.phone,
      gender: isImport ? null : staffForm.gender,
      department: isImport ? null : staffForm.department,
      nin: isImport ? null : staffForm.nin,
      homeAddress: isImport ? null : staffForm.homeAddress,
      profilePhoto: isImport ? null : staffForm.profilePhoto
    }));
    
    setShowPaymentModal(true);
    return true;
  };

  const openPaystackPopup = (reference, amount, email, onSuccess, onClose) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) {
        console.error('❌ PaystackPop not available');
        return reject(new Error('Paystack not loaded'));
      }
      const config = {
        key: PAYSTACK_PUBLIC_KEY,
        email: email,
        amount: amount * 100,
        currency: 'NGN',
        ref: reference,
        meta: {
          product_id: id,
          staff_email: email,
          premium_type: 'hmo_staff',
          department: staffForm.department,
          period_months: selectedPeriod?.months,
          applicant_name: `${staffForm.firstName} ${staffForm.lastName}`,
          applicant_phone: staffForm.phone,
          product_name: product?.name,
          premium_per_staff: activePremium, // ✅ Use activePremium
          is_bulk: pendingStaffData?.isImport || false,
          bulk_count: pendingStaffData?.importCount || 1
        },
        callback: function(response) {
          resolve(response);
          onSuccess?.(response);
        },
        onClose: function() {
          reject(new Error('Payment cancelled'));
          onClose?.();
        }
      };
      try {
        window.PaystackPop.setup(config).openIframe();
      } catch (err) {
        console.error('❌ Error opening Paystack:', err);
        reject(err);
      }
    });
  };

  const saveHmoStaffWithCoverage = async (staffData, paymentData, isImport = false) => {
    try {
      const session = getUserSession();
      const startDate = staffData.CoverageStartDate
        ? new Date(staffData.CoverageStartDate)
        : new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + (paymentData.periodMonths || 1));
      endDate.setDate(endDate.getDate() - 1);
      const payload = {
        CompanyId: session.companyId || 1,
        ProductId: parseInt(id),
        FirstName: staffData.firstName || null,
        LastName: staffData.lastName || null,
        StaffEmail: staffData.email || null,
        Phone: staffData.phone || null,
        Gender: staffData.gender || null,
        Department: staffData.department,
        Nin: staffData.nin || null,
        HomeAddress: staffData.homeAddress || null,
        ProfilePhoto: staffData.profilePhoto || null,
        CoverageStartDate: startDate.toISOString().split('T')[0],
        PeriodMonths: paymentData.periodMonths || 1,
        PaymentReference: paymentData.paymentReference,
        TransactionId: paymentData.transactionId || null,
        TotalPremium: paymentData.premiumAmount,
        Currency: 'NGN',
        PaymentStatus: 'success',
        IsImport: isImport,
        ImportStaffList: isImport ? staffData.importList || [] : null,
      };
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/save-hmo-staff-payment`,
        'POST',
        payload
      );
      if (response?.success) {
        const msg = isImport
          ? `✅ Imported ${response.data?.importCount || 0} staff with coverage ${payload.CoverageStartDate} to ${endDate.toISOString().split('T')[0]}`
          : `✅ Staff enrolled with coverage ${payload.CoverageStartDate} to ${endDate.toISOString().split('T')[0]}`;
        toast.success(msg);
        return response;
      } else {
        throw new Error(response?.message || 'Failed to save HMO staff with coverage');
      }
    } catch (error) {
      console.error('💥 saveHmoStaffWithCoverage error:', error);
      toast.error(error.message || 'Failed to enroll staff with coverage dates');
      throw error;
    }
  };

  const handlePaystackSuccess = async (reference) => {
    try {
      setIsProcessingPayment(true);
      const isImport = pendingStaffData?.isImport || false;
      
      // ✅ FIX: Ensure we use the latest staffForm data if it's a single enrollment
      const staffData = {
        ...pendingStaffData,
        ...(!isImport && {
          firstName: staffForm.firstName,
          lastName: staffForm.lastName,
          email: staffForm.email,
          phone: staffForm.phone,
          gender: staffForm.gender,
          department: staffForm.department,
          nin: staffForm.nin,
          homeAddress: staffForm.homeAddress,
          profilePhoto: staffForm.profilePhoto
        }),
        CoverageStartDate: new Date().toISOString().split('T')[0]
      };
      
      const paymentData = {
        paymentReference: reference.reference,
        transactionId: reference.tranx_id || '',
        premiumAmount: paymentAmount,
        periodMonths: selectedPeriod?.months || 1
      };
      
      await saveHmoStaffWithCoverage(staffData, paymentData, isImport);
      closeStaffModal();
      fetchProductStaff();
    } catch (error) {
      console.error('💥 Enrollment error:', error);
      if (handleAuthError(error)) return;
      if (error.message?.includes('duplicate') || error.message?.includes('already exists')) {
        toast.warning('This staff member may already be enrolled.');
      } else {
        toast.error(error.message || 'Failed to enroll staff. Please contact support.');
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaystackClose = () => {
    toast.info('Premium payment cancelled. Staff was not enrolled.');
    closeStaffModal();
  };

  const handleSubmitStaff = async (e) => {
    e?.preventDefault();
    const success = await preparePremiumPayment(false);
    if (success) {
      // Payment modal will open
    }
  };

  // ============================================================================
  // BULK RENEWAL FUNCTIONALITY
  // ============================================================================
  const [renewalSelection, setRenewalSelection] = useState({
    mode: 'all',
    selectedStaffIds: [],
    renewalPeriod: null,
    renewalPremium: 0
  });

  const openBulkRenewalModal = () => {
    const expiredStaff = staffList.filter(staff => {
      const { isExpired } = calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays);
      return isExpired;
    });
    setRenewalSelection({
      mode: expiredStaff.length > 0 ? 'expired' : 'all',
      selectedStaffIds: expiredStaff.map(s => s.staffId),
      renewalPeriod: null,
      renewalPremium: 0
    });
    setShowBulkRenewalModal(true);
  };

  const handleRenewalModeChange = (mode) => {
    let selectedIds = [];
    if (mode === 'all') {
      selectedIds = staffList.map(s => s.staffId);
    } else if (mode === 'expired') {
      selectedIds = staffList.filter(s => {
        const { isExpired } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
        return isExpired;
      }).map(s => s.staffId);
    } else if (mode === 'expiring') {
      selectedIds = staffList.filter(s => {
        const { isExpiringSoon } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
        return isExpiringSoon;
      }).map(s => s.staffId);
    }
    setRenewalSelection(prev => ({ ...prev, mode, selectedStaffIds: selectedIds }));
  };

  const handleStaffSelectionToggle = (staffId) => {
    setRenewalSelection(prev => {
      const isSelected = prev.selectedStaffIds.includes(staffId);
      const newSelected = isSelected
        ? prev.selectedStaffIds.filter(id => id !== staffId)
        : [...prev.selectedStaffIds, staffId];
      return { ...prev, selectedStaffIds: newSelected };
    });
  };

  const handleRenewalPeriodSelect = (period) => {
    const staffCount = renewalSelection.selectedStaffIds.length;
    const totalPremium = staffCount * activePremium * period.months; // ✅ Use activePremium
    setRenewalSelection(prev => ({
      ...prev,
      renewalPeriod: period,
      renewalPremium: totalPremium
    }));
  };

  const prepareBulkRenewalPayment = async () => {
    if (renewalSelection.selectedStaffIds.length === 0) {
      toast.error('Please select at least one staff member to renew');
      return;
    }
    if (!renewalSelection.renewalPeriod) {
      toast.error('Please select a renewal period');
      return;
    }
    if (!paystackLoaded) {
      toast.warning('Payment gateway is loading. Please wait...');
      return;
    }
    const reference = generateReference();
    setPaymentReference(reference);
    setPaymentAmount(renewalSelection.renewalPremium);
    setPendingStaffData({
      paymentReference: reference,
      premiumAmount: renewalSelection.renewalPremium,
      periodMonths: renewalSelection.renewalPeriod.months,
      periodLabel: renewalSelection.renewalPeriod.label,
      isBulkRenewal: true,
      staffIdsToRenew: renewalSelection.selectedStaffIds,
      email: getUserSession()?.email || 'bulk-renewal@hmo-platform.com'
    });
    setShowBulkRenewalModal(false);
    setShowPaymentModal(true);
  };

  const executeBulkRenewal = async (paymentReference, transactionId) => {
    try {
      const session = getUserSession();
      const startDate = new Date().toISOString().split('T')[0];
      const renewalData = renewalSelection.selectedStaffIds.map(staffId => ({
        Id: parseInt(staffId),
        PremiumAmount: activePremium * renewalSelection.renewalPeriod.months // ✅ Use activePremium
      }));
      const payload = {
        ProductId: parseInt(id),
        RenewalData: renewalData,
        CoverageStartDate: startDate,
        RenewalPeriod: renewalSelection.renewalPeriod.months,
        PaymentReference: paymentReference,
        TransactionId: transactionId,
        TotalAmount: renewalSelection.renewalPremium,
        Currency: 'NGN',
        PaymentStatus: 'success'
      };
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/bulk-renew-hmo-staff`,
        'POST',
        payload
      );
      if (response?.success) {
        toast.success(`✅ Successfully renewed ${response.data?.renewedCount || renewalSelection.selectedStaffIds.length} staff members`);
        fetchProductStaff();
        return true;
      } else {
        throw new Error(response?.message || 'Bulk renewal failed');
      }
    } catch (error) {
      console.error('💥 Bulk renewal error:', error);
      if (handleAuthError(error)) return false;
      toast.error(error.message || 'Failed to renew staff coverage. Please contact support.');
      return false;
    }
  };

  // ============================================================================
  // IMPORT FUNCTIONALITY
  // ============================================================================
  const downloadSampleCSV = () => {
    const headers = ['firstName', 'lastName', 'email', 'phone', 'gender', 'department', 'nin', 'homeAddress'];
    const sampleData = [
      ['John', 'Doe', 'john.doe@example.com', '08012345678', 'male', 'Medical', '12345678901', '123 Lagos Street, Ikeja'],
      ['Jane', 'Smith', 'jane.smith@example.com', '08098765432', 'female', 'Nursing', '23456789012', '456 Abuja Road, Garki'],
      ['Michael', 'Johnson', 'michael.j@example.com', '08123456789', 'male', 'Administration', '34567890123', '789 Port Harcourt Ave'],
    ];
    const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'hmo_staff_sample.csv';
    link.click();
    toast.success('Sample CSV downloaded successfully!');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    setImportFile(file);
    parseFilePreview(file);
    parseImportFile(file);
  };

  const parseFilePreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').slice(0, 6).filter(l => l.trim());
      const preview = lines.map(line => line.split(',').map(cell => cell.trim()));
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  const parseImportFile = (fileToParse) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const staffCount = Math.max(0, lines.length - 1);
      if (staffCount <= 0) {
        toast.error('No valid HMO staff records found in file');
        return;
      }
      setPendingStaffData(prev => ({
        ...prev,
        file: fileToParse,
        staffCount,
        premiumPerStaff: activePremium // ✅ Use activePremium
      }));
      toast.info(`✅ File parsed: ${staffCount} staff records. Select coverage period to continue.`);
    };
    reader.readAsText(fileToParse);
  };

  const handleImportProceed = async () => {
    if (!selectedPeriod) {
      toast.error('Please select a coverage period');
      return;
    }
    const success = await preparePremiumPayment(true);
    if (success) {
      // Payment modal opens
    }
  };

  const handleImportAfterPayment = async (reference) => {
    try {
      setIsProcessingPayment(true);
      const importStaffList = [];
      const fileText = await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = (e) => resolve(e.target.result);
        fr.readAsText(pendingStaffData.file);
      });
      const lines = fileText.split('\n').filter(line => line.trim());
      if (lines.length < 2) throw new Error('Invalid CSV format');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const staff = {};
        headers.forEach((header, idx) => { staff[header] = values[idx] || ''; });
        if (staff.firstname && staff.lastname && staff.email && staff.department) {
          importStaffList.push({
            FirstName: staff.firstname.trim(),
            LastName: staff.lastname.trim(),
            Email: staff.email.toLowerCase().trim(),
            Phone: staff.phone ? staff.phone.replace(/\D/g, '') : null,
            Gender: staff.gender || null,
            Department: staff.department.trim(),
            Nin: staff.nin || null,
            HomeAddress: staff.homeaddress || staff.homeAddress || null
          });
        }
      }
      if (importStaffList.length === 0) {
        throw new Error('No valid staff records found in CSV file');
      }
      const staffData = {
        CoverageStartDate: new Date().toISOString().split('T')[0],
        importList: importStaffList
      };
      const paymentData = {
        paymentReference: reference.reference,
        transactionId: reference.tranx_id || '',
        premiumAmount: paymentAmount,
        periodMonths: pendingStaffData.periodMonths || 1
      };
      await saveHmoStaffWithCoverage(staffData, paymentData, true);
      closeStaffModal();
      fetchProductStaff();
    } catch (error) {
      console.error('💥 Import enrollment error:', error);
      if (handleAuthError(error)) return;
      toast.error(error.message || 'Payment successful but import failed. Please contact support.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // ============================================================================
  // STAFF MANAGEMENT
  // ============================================================================
  const handleDeleteStaff = async (staffId, staffName) => {
    const result = await Swal.fire({
      title: 'Remove HMO Staff?',
      text: `Are you sure you want to remove ${staffName} from this HMO product? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
    });
    if (result.isConfirmed) {
      try {
        const session = getUserSession();
        const res = await authenticatedFetch(
          `${API_BASE}/Settings/remove-staff`,
          'POST',
          { staffId, sessionId: session.sessionId, productId: parseInt(id) }
        );
        if (res?.success === true) {
          toast.success('HMO staff removed successfully');
          fetchProductStaff();
        } else toast.error(res?.message || 'Remove failed');
      } catch (error) {
        console.error('Remove error:', error);
        handleAuthError(error);
        toast.error('Failed to remove HMO staff');
      }
    }
  };

  const handleRenewSingleStaff = async (staff) => {
    setRenewalSelection({
      mode: 'selected',
      selectedStaffIds: [staff.staffId],
      renewalPeriod: null,
      renewalPremium: 0
    });
    setShowBulkRenewalModal(true);
    toast.info('Select renewal period and proceed to payment');
  };

  const filteredStaff = useMemo(() => {
    return staffList.filter(staff => {
      const search = staffSearch.toLowerCase();
      const matchSearch = (
        staff.firstName?.toLowerCase().includes(search) ||
        staff.lastName?.toLowerCase().includes(search) ||
        staff.email?.toLowerCase().includes(search) ||
        staff.department?.toLowerCase().includes(search) ||
        staff.nin?.toLowerCase().includes(search) ||
        staff.homeAddress?.toLowerCase().includes(search)
      );
      const matchDept = !filterDepartment || staff.department === filterDepartment;
      const matchStatus = !filterStatus || staff.status === filterStatus;
      return matchSearch && matchDept && matchStatus;
    });
  }, [staffList, staffSearch, filterDepartment, filterStatus]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const renderPeriodOptions = () => {
    return PAYMENT_PERIODS.map((period) => (
      <div
        key={period.value}
        onClick={() => handlePeriodSelect(period)}
        className="p-3 period-option"
        style={{
          backgroundColor: selectedPeriod?.value === period.value ? '#FFF8F0' : 'white',
          color: selectedPeriod?.value === period.value ? '#F89423' : '#111827',
          cursor: 'pointer',
          borderBottom: '1px solid #f3f4f6'
        }}
      >
        {period.label} - {formatCurrency(activePremium * period.months)} {/* ✅ Use activePremium */}
      </div>
    ));
  };

  const renderPremiumInfo = () => {
    if (selectedPeriod) {
      return (
        <small className="text-muted d-block mt-1">
          ₦{activePremium.toLocaleString()} × {selectedPeriod.months} month{selectedPeriod.months > 1 ? 's' : ''} {/* ✅ Use activePremium */}
          {product?.name && ` • ${product.name}`}
        </small>
      );
    }
    return <small className="text-muted d-block mt-1">Select a period to calculate premium</small>;
  };

  const HospitalList = ({ hospitals }) => {
    if (!hospitals || hospitals.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-hospital-alt fa-3x mb-3 opacity-25"></i>
          <p className="mb-0">No hospitals assigned to this HMO product yet.</p>
        </div>
      );
    }
    return (
      <div className="hospitals-grid">
        {hospitals.map((hospital, index) => (
          <div key={hospital.id || index} className="hospital-card" data-aos="fade-up" data-aos-delay={index * 50}>
            <div className="hospital-header">
              <i className="fas fa-hospital-user me-2"></i>
              <h6 className="mb-0">{hospital.name}</h6>
            </div>
            <div className="hospital-body">
              {hospital.location && <p className="hospital-location mb-2"><i className="fas fa-map-marker-alt me-1 text-orange"></i>{hospital.location}</p>}
              {hospital.address && <p className="hospital-address mb-2 small text-muted"><i className="fas fa-map-pin me-1"></i>{hospital.address}</p>}
              {hospital.contactPhone && <p className="hospital-contact mb-1 small"><i className="fas fa-phone me-1"></i>{hospital.contactPhone}</p>}
              {hospital.contactEmail && <p className="hospital-contact small"><i className="fas fa-envelope me-1"></i>{hospital.contactEmail}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================================
  // NAVIGATION & SIDEBAR HANDLERS
  // ============================================================================
  const handleNavClick = (path, e) => {
    e.preventDefault();
    if (path) navigate(path);
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================
  if (loading) {
    return (
      <div className="product-details-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading HMO product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-details-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-danger fa-3x mb-3"></i>
          <h5>HMO Product Not Found</h5>
          <p className="text-muted mb-4">{error || 'The requested HMO product does not exist.'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/products')}>
            <i className="fas fa-arrow-left me-2"></i>Back to Plans
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="product-details-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        onNavClick={handleNavClick}
        onLogout={handleLogout}
      />
      <div className="main-content">
        <TopHeader
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="HMO Plan Details"
          showBackButton={true}
          onBack={() => navigate('/products')}
        />
        <div className="content-wrapper">
          {/* Page Header */}
          <div className="page-header" data-aos="fade-up">
            <div className="d-flex align-items-center gap-3 mb-3">
              <button className="btn-back" onClick={() => navigate('/products')}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <h2 className="mb-0">{product.name}</h2>
            </div>
            <div className="tabs">
              <button className={`tab ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
                Details
              </button>
              <button className={`tab ${activeTab === 'list_of_staff' ? 'active' : ''}`} onClick={() => setActiveTab('list_of_staff')}>
                HMO Staff List
              </button>
            </div>
          </div>

          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              {/* Product Info */}
              <div className="product-info-section" data-aos="fade-up">
                <div className="product-status">
                  <span className="status-label">HMO Plan Status</span>
                  <span className={`status-badge ${product.status === 'Active' ? 'active' : 'inactive'}`}>
                    {product.status}
                  </span>
                </div>
                <div className="product-main-info">
                  <div className="product-icon"><i className="fas fa-heartbeat"></i></div>
                  <div className="product-info">
                    <div className="info-row">
                      <div className="info-item">
                        <span className="label">{product.category}</span>
                        <h4 className="value">{product.name}</h4>
                      </div>
                      <div className="info-item">
                        <span className="label">Provider</span>
                        <h4 className="value">{product.provider}</h4>
                      </div>
                      <div className="info-item">
                        <span className="label">Minimum Premium</span>
                        <h4 className="value premium">
                          {product.minimumPremium}
                          <small className="text-muted">/staff/month</small>
                        </h4>
                      </div>
                      <div className="info-item">
                        <span className="label">Family Premium</span>
                        <h4 className="value premium" style={{ color: '#8b5cf6' }}>
                          {product.familyPremium}
                          <small className="text-muted">/family/month</small>
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="stats-row" data-aos="fade-up">
                <div className="stat-card"><span className="stat-label">Gross Amount</span><h3 className="stat-value">{product.grossAmount}</h3></div>
                <div className="stat-card success"><span className="stat-label">Total Commissions</span><h3 className="stat-value">{product.totalCommissions}</h3></div>
                <div className="stat-card primary"><span className="stat-label">Total Sales</span><h3 className="stat-value">{product.totalSales}</h3></div>
                <div className="stat-card warning"><span className="stat-label">Total Customers</span><h3 className="stat-value">{product.totalCustomers}</h3></div>
              </div>

              {/* Details Grid */}
              <div className="details-grid">
                <div className="details-main">
                  <div className="card" data-aos="fade-up">
                    <div className="card-header">
                      <h5 className="mb-0">HMO Details</h5>
                    </div>
                    <div className="details-tabs">
                      {['policy', 'benefits', 'howItWorks', 'howToClaim', 'hospitalList'].map(tab => (
                        <button key={tab} className={`details-tab ${activeDetailsTab === tab ? 'active' : ''}`} onClick={() => setActiveDetailsTab(tab)}>
                          {tab === 'policy' && 'Policy Document'}
                          {tab === 'benefits' && 'HMO Benefits'}
                          {tab === 'howItWorks' && 'How It Works'}
                          {tab === 'howToClaim' && 'How to Claim'}
                          {tab === 'hospitalList' && `Network Hospitals (${product.hospitals?.length || 0})`}
                        </button>
                      ))}
                    </div>
                    <div className="details-content">
                      {activeDetailsTab === 'benefits' && (
                        <div className="benefits-content">
                          <h6>HMO Product Benefits</h6>
                          <div className="benefits-text">
                            {product.benefits.split('\n').map((paragraph, idx) => (
                              <div key={idx} className="benefit-section">
                                {paragraph.split('\n').map((line, lineIdx) => (
                                  line.trim() && <p key={lineIdx} className={lineIdx === 0 ? 'benefit-title' : 'benefit-desc'}>{line}</p>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {activeDetailsTab === 'policy' && (
                        <div className="policy-document-container">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Policy Document</h6>
                            {product.policyDocument && (
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => downloadPDF(product.policyDocument, `${product.name}_Policy.pdf`)}
                                >
                                  <i className="fas fa-download me-1" /> Download PDF
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => viewPDF(product.policyDocument)}
                                >
                                  <i className="fas fa-eye me-1" /> View
                                </button>
                              </div>
                            )}
                          </div>
                          {product.policyDocument ? (
                            <div className="text-center py-5" style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: '#f8f9fa'
                            }}>
                              <i className="fas fa-file-pdf fa-4x text-danger mb-3"></i>
                              <h5 className="mb-2">Policy Document Available</h5>
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  className="btn btn-primary"
                                  onClick={() => downloadPDF(product.policyDocument, `${product.name}_Policy.pdf`)}
                                >
                                  <i className="fas fa-download me-2" /> Download
                                </button>
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => viewPDF(product.policyDocument)}
                                >
                                  <i className="fas fa-external-link-alt me-2" /> View in Browser
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-5 text-muted">
                              <i className="fas fa-file-alt fa-4x mb-3 opacity-25"></i>
                              <p className="mb-0">No policy document available.</p>
                            </div>
                          )}
                        </div>
                      )}
                      {activeDetailsTab === 'howItWorks' && <p className="text-muted">{product.howItWorks}</p>}
                      {activeDetailsTab === 'howToClaim' && <p className="text-muted">{product.howToClaim}</p>}
                      {activeDetailsTab === 'hospitalList' && <HospitalList hospitals={product.hospitals} />}
                    </div>
                  </div>
                </div>
                <div className="details-sidebar">
                  <div className="card customers-card" data-aos="fade-up">
                    <div className="card-header">
                      <h6 className="mb-1">Enrolled Staff</h6>
                      <small className="text-muted">{product.totalCustomers} Active enrollees</small>
                    </div>
                    <div className="customers-tabs">
                      <button className="customers-tab active">Active Enrollees</button>
                    </div>
                    {product.totalCustomers === 0 ? (
                      <div className="customers-empty">
                        <div className="empty-icon"><i className="fas fa-users"></i></div>
                        <h6>No Enrolled Staff</h6>
                        <p className="text-muted small">Add staff to this HMO product to get started.</p>
                      </div>
                    ) : (
                      <div className="customers-list">
                        <p className="text-muted small p-3">Enrolled staff list will appear here...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* HMO STAFF LIST TAB */}
          {activeTab === 'list_of_staff' && (
            <div className="card" data-aos="fade-up">
              <div className="card-body p-4 p-md-5">
                {/* Premium Summary Banner */}
                <div className="alert alert-primary border-0 mb-4" style={{ backgroundColor: '#e7f1ff' }}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                      <h6 className="mb-1">💰 HMO Premium Calculation</h6>
                      <p className="mb-0 small text-muted">
                        {staffSummary.totalStaff} staff × ₦{premiumPerStaff.toLocaleString()}/month =
                        <strong> {formatCurrency(staffSummary.totalPremium)}/month</strong>
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      {staffSummary.expiredCount > 0 && (
                        <button className="btn btn-sm btn-warning" onClick={openBulkRenewalModal}>
                          <i className="fas fa-sync-alt me-1"></i>Renew {staffSummary.expiredCount} Expired
                        </button>
                      )}
                      <button className="btn btn-sm btn-primary" onClick={() => setShowPaymentModal(true)} disabled={staffSummary.totalStaff === 0}>
                        <i className="fas fa-credit-card me-1"></i>Pay Premium ({formatCurrency(staffSummary.totalPremium)})
                      </button>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3 col-6">
                    <div className="card border-0 bg-primary bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-primary text-white rounded-circle p-2"><i className="fas fa-user-md fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Total HMO Staff</small>
                            <h5 className="mb-0 fw-bold">{staffSummary.totalStaff}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="card border-0 bg-success bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-success text-white rounded-circle p-2"><i className="fas fa-check-circle fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Active Enrollees</small>
                            <h5 className="mb-0 fw-bold">{staffSummary.activeStaff}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="card border-0 bg-warning bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-warning text-white rounded-circle p-2"><i className="fas fa-money-bill-wave fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Monthly Premium</small>
                            <h5 className="mb-0 fw-bold">{formatCurrency(staffSummary.totalPremium)}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="card border-0 bg-info bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-info text-white rounded-circle p-2"><i className="fas fa-calculator fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Per Staff/Month</small>
                            <h5 className="mb-0 fw-bold">₦{premiumPerStaff.toLocaleString()}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Department Breakdown */}
                {Object.keys(staffSummary.byDepartment).length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">📊 By HMO Department</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(staffSummary.byDepartment).map(([dept, data]) => {
                        const config = HMO_DEPARTMENTS[dept] || { color: 'secondary', icon: 'fa-building' };
                        return (
                          <button
                            key={dept}
                            className={`btn btn-sm btn-outline-${config.color} d-flex align-items-center gap-2 ${filterDepartment === dept ? 'active' : ''}`}
                            onClick={() => setFilterDepartment(filterDepartment === dept ? '' : dept)}
                          >
                            <i className={`fas fa-${config.icon}`}></i>
                            <span>{dept}</span>
                            <span className="badge bg-light text-dark">{data.count}</span>
                            <small className="text-muted">({formatCurrency(data.premium)}/mo)</small>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Header & Actions */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                  <div>
                    <h5 className="mb-1">HMO Staff for {product.name}</h5>
                    <p className="text-muted mb-0 small">
                      {filteredStaff.length} staff • Premium: {formatCurrency(filteredStaff.length * premiumPerStaff)}/month
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-success" onClick={() => setShowImportModal(true)}>
                      <i className="fas fa-file-upload me-2"></i>Upload CSV
                    </button>
                    <button className="btn btn-outline-primary" onClick={downloadSampleCSV}>
                      <i className="fas fa-download me-2"></i>Download Sample
                    </button>
                    <button className="btn btn-primary" onClick={openAddStaffModal}>
                      <i className="fas fa-plus me-2"></i>Add HMO Staff & Pay
                    </button>
                  </div>
                </div>

                {/* Search & Filters */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search by name, email, NIN, address..."
                        value={staffSearch}
                        onChange={(e) => setStaffSearch(e.target.value)}
                      />
                      {staffSearch && (
                        <button className="btn btn-outline-secondary" onClick={() => setStaffSearch('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <select className="form-select" value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
                      <option value="">All Departments</option>
                      {Object.keys(HMO_DEPARTMENTS).map(dept => <option key={dept} value={dept}>{dept}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                      <option value="onLeave">On Leave</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>

                {/* Data Table with Coverage Countdown & Photo Upload */}
                <div className="table-responsive">
                  {staffLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-orange" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2">Loading HMO staff records...</p>
                    </div>
                  ) : (
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th><input type="checkbox" className="form-check-input" onChange={(e) => {
                            const checked = e.target.checked;
                            setRenewalSelection(prev => ({
                              ...prev,
                              selectedStaffIds: checked ? filteredStaff.map(s => s.staffId) : []
                            }));
                          }} checked={renewalSelection.selectedStaffIds.length === filteredStaff.length && filteredStaff.length > 0} title="Select all for bulk renewal" /></th>
                          <th>HMO Staff</th>
                          <th>Department</th>
                          <th className="d-none d-lg-table-cell">NIN</th>
                          <th className="d-none d-xl-table-cell">Home Address</th>
                          <th className="d-none d-md-table-cell">Coverage Period</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStaff.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <div className="text-muted">
                                <i className="fas fa-user-md fa-3x mb-3 d-block opacity-25"></i>
                                <p className="mb-0">{staffSearch ? 'No HMO staff match your search' : 'No staff enrolled in this HMO product yet'}</p>
                                <small className="text-muted">{staffSearch ? 'Try different keywords' : 'Click "Add HMO Staff & Pay" to enrol staff'}</small>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredStaff.map((staff) => {
                            const deptConfig = HMO_DEPARTMENTS[staff.department] || { color: 'secondary', icon: 'fa-building' };
                            const { remainingDays, isExpired, isExpiringSoon, countdown, status: coverageStatus } = calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays);
                            const startDate = formatDate(staff.coverageStartDate);
                            const endDate = formatDate(staff.coverageEndDate);
                            const isSelected = renewalSelection.selectedStaffIds.includes(staff.staffId);
                            return (
                              <tr key={staff.staffId || staff.id} className={isExpired ? 'table-danger' : isExpiringSoon ? 'table-warning' : ''} style={{ transition: 'background-color 0.3s ease' }}>
                                <td><input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => handleStaffSelectionToggle(staff.staffId)} title="Select for bulk renewal" /></td>
                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="avatar-wrapper position-relative">
                                      {staff.profilePhoto ? (
                                        <img
                                          src={staff.profilePhoto.startsWith('data:')
                                            ? staff.profilePhoto
                                            : `data:image/jpeg;base64,${staff.profilePhoto}`}
                                          alt={`${staff.firstName} ${staff.lastName}`}
                                          className="rounded-circle border border-2 border-white shadow-sm"
                                          style={{
                                            width: '42px',
                                            height: '42px',
                                            objectFit: 'cover',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling?.style.setProperty('display', 'flex', 'important');
                                          }}
                                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                        />
                                      ) : null}
                                      <div
                                        className={`bg-gradient-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm ${staff.profilePhoto ? 'd-none' : ''}`}
                                        style={{
                                          width: '42px',
                                          height: '42px',
                                          fontSize: '14px',
                                          position: staff.profilePhoto ? 'absolute' : 'relative',
                                          transition: 'transform 0.2s ease',
                                          cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                      >
                                        {staff.firstName?.[0]?.toUpperCase()}{staff.lastName?.[0]?.toUpperCase()}
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary position-absolute shadow-sm avatar-upload-btn"
                                        style={{
                                          bottom: '-4px',
                                          right: '-4px',
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          padding: 0,
                                          fontSize: '10px',
                                          border: '2px solid white',
                                          backgroundColor: 'white',
                                          color: '#F89423',
                                          display: 'none',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.2s ease',
                                          zIndex: 10
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const fileInput = document.getElementById(`staff-photo-${staff.staffId}`);
                                          if (fileInput) fileInput.click();
                                        }}
                                        title="Upload/Change Photo"
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#F89423';
                                          e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'white';
                                          e.currentTarget.style.color = '#F89423';
                                        }}
                                      >
                                        <i className="fas fa-camera"></i>
                                      </button>
                                      <input
                                        type="file"
                                        id={`staff-photo-${staff.staffId}`}
                                        className="d-none"
                                        accept="image/jpeg,image/png,image/jpg"
                                        onChange={(e) => handleStaffPhotoUploadInTable(e, staff.staffId)}
                                      />
                                    </div>
                                    <div>
                                      <div className="fw-semibold">{staff.firstName} {staff.lastName}</div>
                                      <small className="text-muted d-none d-md-block">{staff.email}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge bg-${deptConfig.color} bg-opacity-10 text-dark border border-${deptConfig.color}`}>
                                    <i className={`fas fa-${deptConfig.icon} me-1`}></i>{staff.department || '-'}
                                  </span>
                                </td>
                                <td className="d-none d-lg-table-cell"><small className="text-muted">{staff.nin || '-'}</small></td>
                                <td className="d-none d-xl-table-cell">
                                  <small className="text-muted text-truncate" style={{ maxWidth: '150px', display: 'inline-block' }}>
                                    {staff.homeAddress || '-'}
                                  </small>
                                </td>
                                <td className="d-none d-md-table-cell">
                                  <div className="coverage-card p-2 rounded" style={{ backgroundColor: '#f8f9fa', borderLeft: `4px solid ${isExpired ? '#dc3545' : isExpiringSoon ? '#ffc107' : '#198754'}` }}>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <i className="fas fa-play-circle text-success" style={{ fontSize: '10px' }} title="Coverage Start"></i>
                                      <small className="text-muted">Start:</small>
                                      <span className="fw-semibold small" title={staff.coverageStartDate}>{startDate}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <i className="fas fa-stop-circle text-danger" style={{ fontSize: '10px' }} title="Coverage End"></i>
                                      <small className="text-muted">End:</small>
                                      <span className="fw-semibold small" title={staff.coverageEndDate}>{endDate}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                      <span className={`badge ${isExpired ? 'bg-danger' : isExpiringSoon ? 'bg-warning text-dark' : 'bg-success'}`}>
                                        <i className="fas fa-clock me-1"></i>{staff.coverageDays || 0} days
                                      </span>
                                      {countdown && (
                                        <small className={`fw-semibold ${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-success'}`}>
                                          {isExpired ? `❌ Expired` : `⏱ ${countdown}`}
                                        </small>
                                      )}
                                    </div>
                                    {isExpiringSoon && !isExpired && (
                                      <small className="text-warning d-block mt-1">
                                        <i className="fas fa-exclamation-triangle me-1"></i>Renew soon to avoid lapse
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td><span className={`badge bg-${getStatusColor(staff.status)}`}>{staff.status?.charAt(0).toUpperCase() + staff.status?.slice(1)}</span></td>
                                <td className="text-end">
                                  <div className="btn-group">
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEditStaffModal(staff)} title="Edit Staff"><i className="fas fa-edit"></i></button>
                                    <button className="btn btn-sm btn-outline-info" onClick={() => openStaffDependentsModal(staff)} title="Manage Dependents"><i className="fas fa-users"></i></button>
                                    {isExpired && <button className="btn btn-sm btn-outline-warning" title="Renew Coverage" onClick={() => handleRenewSingleStaff(staff)}><i className="fas fa-sync-alt"></i></button>}
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteStaff(staff.staffId || staff.id, `${staff.firstName} ${staff.lastName}`)} title="Remove Staff"><i className="fas fa-trash"></i></button>
                                    <Link
                                      to={`/staff/${staff.staffId}/dependents`}
                                      className="btn btn-sm btn-outline-info"
                                      title="View Dependents"
                                    >
                                      <i className="fas fa-users"></i>
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="5" className="fw-bold text-end">MONTHLY PREMIUM:</td>
                          <td className="fw-bold text-success">{formatCurrency(filteredStaff.length * premiumPerStaff)}</td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>

                {/* Bulk Actions Bar */}
                {renewalSelection.selectedStaffIds.length > 0 && (
                  <div className="mt-3 p-3 bg-light rounded d-flex align-items-center justify-content-between">
                    <span className="text-muted"><i className="fas fa-users me-2"></i>{renewalSelection.selectedStaffIds.length} staff selected for renewal</span>
                    <button className="btn btn-warning btn-sm" onClick={openBulkRenewalModal}>
                      <i className="fas fa-sync-alt me-2"></i>Renew Selected ({renewalSelection.selectedStaffIds.length})
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add Dependent Tab */}
          {activeTab === 'add_dependent' && (
            <div className="card" data-aos="fade-up">
              <div className="card-body p-4 p-md-5">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-1">👨‍👩‍👦 Manage Dependents</h5>
                    <p className="text-muted mb-0 small">View and manage dependents. Click "Add Dependent" to create new entries.</p>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary" onClick={() => toast.info('Select a staff member from the Staff List tab to load their dependents.')}>
                      <i className="fas fa-sync-alt me-2"></i>Refresh List
                    </button>
                    <button className="btn btn-primary" onClick={openDependentsModal} disabled={dependentsList.length >= MAX_DEPENDENTS}>
                      <i className="fas fa-plus me-2"></i>Add Dependent ({dependentsList.length}/{MAX_DEPENDENTS})
                    </button>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4 col-6">
                    <div className="card border-0 bg-primary bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-primary text-white rounded-circle p-2"><i className="fas fa-users fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Total Dependents</small>
                            <h5 className="mb-0 fw-bold">{dependentsList.length}/{MAX_DEPENDENTS}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 col-6">
                    <div className="card border-0 bg-success bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-success text-white rounded-circle p-2"><i className="fas fa-user-check fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Active</small>
                            <h5 className="mb-0 fw-bold">{dependentsList.filter(d => d.age > 0).length}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 col-6">
                    <div className="card border-0 bg-info bg-opacity-10">
                      <div className="card-body py-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-info text-white rounded-circle p-2"><i className="fas fa-heartbeat fa-sm"></i></div>
                          <div>
                            <small className="text-muted d-block">Covered</small>
                            <h5 className="mb-0 fw-bold">{dependentsList.length}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dependents Table */}
                <div className="table-responsive">
                  {dependentsLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                      <p className="text-muted mt-2">Loading dependents...</p>
                    </div>
                  ) : dependentsList.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-users fa-4x mb-3 text-muted opacity-25"></i>
                      <h6 className="text-muted">No dependents loaded</h6>
                      <p className="text-muted small mb-3">Select a staff member from the "HMO Staff List" tab and click Edit to load their dependents, or add new ones.</p>
                      <button className="btn btn-outline-primary" onClick={openDependentsModal}>
                        <i className="fas fa-plus me-2"></i>Add New Dependent
                      </button>
                    </div>
                  ) : (
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Full Name</th>
                          <th>Relationship</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>Contact</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dependentsList.map((dep, index) => (
                          <tr key={dep.id}>
                            <td className="fw-bold text-muted">{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {dep.photo ? (
                                  <img src={dep.photo} alt={dep.firstName} className="rounded-circle" style={{ width: '32px', height: '32px', objectFit: 'cover' }} />
                                ) : (
                                  <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', fontSize: '12px' }}>
                                    {dep.firstName?.[0]}{dep.lastName?.[0]}
                                  </div>
                                )}
                                <div>
                                  <div className="fw-semibold">{dep.firstName} {dep.lastName}</div>
                                  {dep.email && <small className="text-muted d-none d-md-block">{dep.email}</small>}
                                </div>
                              </div>
                            </td>
                            <td><span className="badge bg-secondary">{RELATIONSHIP_OPTIONS.find(r => r.value === dep.relationship)?.label || dep.relationship}</span></td>
                            <td>{dep.age || '-'}</td>
                            <td><span className={`badge ${dep.gender === 'male' ? 'bg-primary' : dep.gender === 'female' ? 'bg-pink' : 'bg-secondary'}`}>{dep.gender || '-'}</span></td>
                            <td>
                              <small className="d-block">{dep.phone || '-'}</small>
                              <small className="text-muted">{dep.email || '-'}</small>
                            </td>
                            <td className="text-end">
                              <div className="btn-group">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditDependent(dep)}><i className="fas fa-edit"></i></button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteDependent(dep.id)}><i className="fas fa-trash"></i></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit HMO Staff Modal - with photo upload */}
      {showAddStaffModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <form onSubmit={handleSubmitStaff}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingStaff ? '✏️ Edit HMO Staff' : '➕ Enrol HMO Staff'} in {product.name}</h5>
                  <button type="button" className="btn-close" onClick={closeStaffModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">First Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="firstName" value={staffForm.firstName} onChange={(e) => setStaffForm(prev => ({ ...prev, firstName: e.target.value }))} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Last Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="lastName" value={staffForm.lastName} onChange={(e) => setStaffForm(prev => ({ ...prev, lastName: e.target.value }))} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Email Address <span className="text-danger">*</span></label>
                      <input type="email" className="form-control" name="email" value={staffForm.email} onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))} required pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$" title="Please enter a valid email address" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Phone Number</label>
                      <input type="tel" className="form-control" name="phone" value={staffForm.phone} onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="8012345678" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">NIN (National Identity Number)</label>
                      <input type="text" className="form-control" name="nin" value={staffForm.nin} onChange={(e) => setStaffForm(prev => ({ ...prev, nin: e.target.value }))} placeholder="11-digit NIN" maxLength={11} pattern="\d{11}" title="Please enter a valid 11-digit NIN" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Home Address</label>
                      <input type="text" className="form-control" name="homeAddress" value={staffForm.homeAddress} onChange={(e) => setStaffForm(prev => ({ ...prev, homeAddress: e.target.value }))} placeholder="e.g., 123 Lagos Street, Ikeja" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Gender</label>
                      <select className="form-select" name="gender" value={staffForm.gender} onChange={(e) => setStaffForm(prev => ({ ...prev, gender: e.target.value }))}>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Department <span className="text-danger">*</span></label>
                      <select className="form-select" name="department" value={staffForm.department} onChange={(e) => setStaffForm(prev => ({ ...prev, department: e.target.value }))} required>
                        <option value="">Select Department</option>
                        {Object.keys(HMO_DEPARTMENTS).map(dept => <option key={dept} value={dept}>{dept}</option>)}
                      </select>
                    </div>

                    {/* Staff Photo Upload */}
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Profile Photo</label>
                      <div className="d-flex align-items-center gap-3">
                        {staffForm.profilePhoto ? (
                          <img src={staffForm.profilePhoto} alt="Preview" className="rounded-circle" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                        ) : (
                          <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                            <i className="fas fa-user text-muted"></i>
                          </div>
                        )}
                        <div>
                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => staffPhotoInputRef.current?.click()}>
                            <i className="fas fa-upload me-1"></i>Upload Photo
                          </button>
                          {staffForm.profilePhoto && (
                            <button type="button" className="btn btn-outline-danger btn-sm ms-2" onClick={() => setStaffForm(prev => ({ ...prev, profilePhoto: null }))}>
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                          <input type="file" ref={staffPhotoInputRef} className="d-none" accept="image/jpeg,image/png,image/jpg" onChange={handleStaffPhotoUpload} />
                          <small className="text-muted d-block mt-1">JPG/PNG, max 2MB</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dependents Section - MAX 3 */}
                  <div className="mt-4 pt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">👨‍👩‍👧‍👦 Dependents (Max {MAX_DEPENDENTS})</h6>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={openDependentsModal} disabled={dependentsList.length >= MAX_DEPENDENTS}>
                        <i className="fas fa-plus me-1"></i>Add Dependent
                      </button>
                    </div>
                    {dependentsList.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Name</th>
                              <th>Relationship</th>
                              <th>Age</th>
                              <th>Gender</th>
                              <th>Contact</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dependentsList.map((dep) => (
                              <tr key={dep.id}>
                                <td>{dep.firstName} {dep.lastName}</td>
                                <td><span className="badge bg-secondary">{RELATIONSHIP_OPTIONS.find(r => r.value === dep.relationship)?.label || dep.relationship}</span></td>
                                <td>{dep.age || '-'}</td>
                                <td>{dep.gender || '-'}</td>
                                <td>
                                  <small className="d-block">{dep.phone || '-'}</small>
                                  <small className="text-muted">{dep.email || '-'}</small>
                                </td>
                                <td className="text-end">
                                  <button type="button" className="btn btn-sm btn-link text-primary p-0 me-2" onClick={() => handleEditDependent(dep)}><i className="fas fa-edit"></i></button>
                                  <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteDependent(dep.id)}><i className="fas fa-trash"></i></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-3 text-muted border rounded">
                        <i className="fas fa-users fa-2x mb-2 d-block opacity-50"></i>
                        <p className="mb-0 small">No dependents added yet</p>
                        <small>Click "Add Dependent" to include family members (max {MAX_DEPENDENTS})</small>
                      </div>
                    )}
                  </div>

                  {/* ✅ NEW: Premium Type Selection */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">💳 Select Premium Type</h6>
                  <div className="mb-3">
                    <div className="d-flex gap-3 flex-wrap">
                      <div 
                        className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'minimum' ? 'border-primary bg-light' : ''}`} 
                        style={{cursor: 'pointer', minWidth: '200px'}} 
                        onClick={() => setSelectedPremiumType('minimum')}
                      >
                        <input className="form-check-input" type="radio" name="premiumTypeAdd" id="premiumMinimumAdd" value="minimum" checked={selectedPremiumType === 'minimum'} onChange={() => setSelectedPremiumType('minimum')} />
                        <label className="form-check-label w-100" htmlFor="premiumMinimumAdd">
                          <strong>Minimum Premium</strong>
                          <small className="d-block text-muted">{formatCurrency(product?.minimumPremiumValue || 0)} / month</small>
                        </label>
                      </div>
                      <div 
                        className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'family' ? 'border-primary bg-light' : ''}`} 
                        style={{cursor: 'pointer', minWidth: '200px'}} 
                        onClick={() => setSelectedPremiumType('family')}
                      >
                        <input className="form-check-input" type="radio" name="premiumTypeAdd" id="premiumFamilyAdd" value="family" checked={selectedPremiumType === 'family'} onChange={() => setSelectedPremiumType('family')} />
                        <label className="form-check-label w-100" htmlFor="premiumFamilyAdd">
                          <strong>Family Premium</strong>
                          <small className="d-block text-muted">{formatCurrency(product?.familyPremiumValue || 0)} / month</small>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Period Selection */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">📅 Period of Cover & Premium</h6>
                  <div className="mb-3 period-dropdown">
                    <label className="form-label small fw-semibold">Select Coverage Period <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ border: `2px solid ${showPeriodDropdown ? '#F89423' : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>{selectedPeriod ? selectedPeriod.label : 'Select payment plan'}</span>
                        <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                      </div>
                      {showPeriodDropdown && (
                        <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                          {renderPeriodOptions()}
                        </div>
                      )}
                    </div>
                    {selectedPeriod && renderPremiumInfo()}
                  </div>

                  {/* Premium Info */}
                  <div className="alert alert-info border-0 mt-3" style={{ backgroundColor: '#f0f9ff' }}>
                    <h6 className="mb-2">💰 Premium Summary</h6>
                    <div className="row small">
                      <div className="col-6"><span className="text-muted">Per Month:</span> <span className="fw-semibold ms-2">₦{activePremium.toLocaleString()}</span></div>
                      <div className="col-6"><span className="text-muted">Total ({selectedPeriod?.label || '1 Month'}):</span> <span className="fw-semibold ms-2 text-success">{selectedPeriod ? formatCurrency(activePremium * selectedPeriod.months) : '₦0'}</span></div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      <i className="fas fa-info-circle me-1"></i>Payment via Paystack is required before staff can be enrolled
                    </small>
                  </div>
                </div>
                <div className="modal-footer border-top">
                  <button type="button" className="btn btn-secondary" onClick={closeStaffModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={!selectedPeriod || !paystackLoaded}>
                    {!paystackLoaded ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Loading Payment...</>
                    ) : !selectedPeriod ? (
                      'Select Coverage Period'
                    ) : (
                      <><i className="fas fa-lock me-2"></i>Pay {formatCurrency(paymentAmount || activePremium)} & Enrol</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* DEPENDENTS MODAL - MAX 3 + PHOTO + DOB + SPINNER */}
      {showDependentsModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleAddDependent}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingDependentId ? '✏️ Edit Dependent' : '➕ Add Dependent'}</h5>
                  <button type="button" className="btn-close" onClick={closeDependentsModal} disabled={isSavingDependent}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">First Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="firstName" value={newDependent.firstName} onChange={handleDependentChange} required disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Last Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="lastName" value={newDependent.lastName} onChange={handleDependentChange} required disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Relationship <span className="text-danger">*</span></label>
                      <select className="form-select" name="relationship" value={newDependent.relationship} onChange={handleDependentChange} required disabled={isSavingDependent}>
                        <option value="">Select Relationship</option>
                        {RELATIONSHIP_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Age</label>
                      <input type="number" className="form-control" name="age" value={newDependent.age} onChange={handleDependentChange} min="0" max="120" disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Gender</label>
                      <select className="form-select" name="gender" value={newDependent.gender} onChange={handleDependentChange} disabled={isSavingDependent}>
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Phone</label>
                      <input type="tel" className="form-control" name="phone" value={newDependent.phone} onChange={handleDependentChange} placeholder="08012345678" disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Email</label>
                      <input type="email" className="form-control" name="email" value={newDependent.email} onChange={handleDependentChange} placeholder="email@example.com" disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Date of Birth</label>
                      <input type="date" className="form-control" name="dateOfBirth" value={newDependent.dateOfBirth} onChange={handleDependentChange} disabled={isSavingDependent} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Photo</label>
                      <div className="d-flex align-items-center gap-2">
                        {newDependent.photo ? (
                          <img src={newDependent.photo} alt="Preview" className="rounded-circle" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                        ) : (
                          <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}><i className="fas fa-user text-muted"></i></div>
                        )}
                        <input type="file" name="photo" className="form-control form-control-sm" accept="image/*" onChange={handleDependentChange} disabled={isSavingDependent} />
                      </div>
                    </div>
                  </div>

                  {/* Max dependents warning */}
                  {dependentsList.length >= MAX_DEPENDENTS && !editingDependentId && (
                    <div className="alert alert-warning mt-3 mb-0 small">
                      <i className="fas fa-exclamation-triangle me-1"></i>Maximum {MAX_DEPENDENTS} dependents allowed per staff member
                    </div>
                  )}
                </div>
                <div className="modal-footer border-top">
                  <button type="button" className="btn btn-secondary" onClick={closeDependentsModal} disabled={isSavingDependent}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSavingDependent || (dependentsList.length >= MAX_DEPENDENTS && !editingDependentId)}>
                    {isSavingDependent ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {editingDependentId ? 'Updating Dependent...' : 'Adding Dependent...'}
                      </>
                    ) : (
                      editingDependentId ? 'Update Dependent' : 'Add Dependent'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title">💳 Confirm HMO Premium Payment</h5>
                <button type="button" className="btn-close" onClick={closeStaffModal}></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-4">
                  <i className="fas fa-heartbeat text-primary fa-4x mb-3"></i>
                  <h5>Pay HMO Premium via Paystack</h5>
                  <p className="text-muted">Secure payment for staff enrolment</p>
                </div>
                <div className="card bg-light border-0 mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Staff Count:</span>
                      <span className="fw-bold">{pendingStaffData?.importCount || pendingStaffData?.staffCount || 1}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Coverage Period:</span>
                      <span className="fw-bold">{pendingStaffData?.periodLabel || selectedPeriod?.label || '1 Month'}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Premium per Staff/Month:</span>
                      <span className="fw-bold">₦{activePremium.toLocaleString()}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span className="fw-semibold">Total Premium:</span>
                      <span className="fw-bold text-success fs-5">{formatCurrency(paymentAmount)}</span>
                    </div>
                  </div>
                </div>
                <button className="btn btn-success btn-lg w-100 mb-3" onClick={async () => {
                  if (!paystackLoaded) { toast.warning('Payment gateway is loading. Please wait...'); return; }
                  const isImportFlow = pendingStaffData?.isImport;
                  const isBulkRenewal = pendingStaffData?.isBulkRenewal;
                  const emailForPayment = isImportFlow
                    ? (getUserSession()?.email || 'import@hmo-platform.com')
                    : pendingStaffData?.email || staffForm?.email || getUserSession()?.email;
                  if (!emailForPayment || !emailForPayment.includes('@')) {
                    toast.error('A valid email is required for payment processing');
                    return;
                  }
                  try {
                    const response = await openPaystackPopup(paymentReference, paymentAmount, emailForPayment, async (ref) => {
                      if (isBulkRenewal) {
                        await executeBulkRenewal(ref.reference, ref.tranx_id);
                      } else if (isImportFlow) {
                        await handleImportAfterPayment(ref);
                      } else {
                        await handlePaystackSuccess(ref);
                      }
                    }, handlePaystackClose);
                  } catch (err) {
                    if (err.message !== 'Payment cancelled') {
                      console.error('❌ Payment error:', err);
                      toast.error(err.message || 'Payment failed. Please try again.');
                    }
                  }
                }} disabled={isProcessingPayment || !paystackLoaded} style={{ opacity: (isProcessingPayment || !paystackLoaded) ? 0.7 : 1, cursor: (isProcessingPayment || !paystackLoaded) ? 'not-allowed' : 'pointer' }}>
                  {isProcessingPayment ? (
                    <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Processing...</>
                  ) : (
                    <><i className="fas fa-credit-card me-2"></i>Pay {formatCurrency(paymentAmount)} Now</>
                  )}
                </button>
                <small className="text-muted d-block"><i className="fas fa-lock me-1"></i>Your HMO premium payment is secure and encrypted</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title">📥 Import HMO Staff from CSV</h5>
                <button type="button" className="btn-close" onClick={closeStaffModal}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info border-0 mb-4">
                  <h6 className="mb-2">📋 CSV File Format</h6>
                  <p className="small mb-2"><strong>Required Columns:</strong> firstName, lastName, email, phone, gender, department, nin, homeAddress</p>
                  <ul className="small mb-2">
                    <li><strong>firstName:</strong> Staff first name (e.g., John)</li>
                    <li><strong>lastName:</strong> Staff last name (e.g., Doe)</li>
                    <li><strong>email:</strong> Valid email address (e.g., john@example.com)</li>
                    <li><strong>phone:</strong> Phone number (e.g., 08012345678)</li>
                    <li><strong>gender:</strong> male, female, or other</li>
                    <li><strong>department:</strong> Must match: Medical, Nursing, Administration, Pharmacy, Laboratory, Records, Billing, Customer Care</li>
                    <li><strong>nin:</strong> 11-digit National Identity Number (optional)</li>
                    <li><strong>homeAddress:</strong> Full home address (optional)</li>
                  </ul>
                  <p className="small mb-0"><strong>Premium:</strong> {activePremium.toLocaleString()}/staff/month will be charged</p>
                </div>
                <div className="d-flex gap-2 mb-4">
                  <button className="btn btn-outline-primary" onClick={downloadSampleCSV}><i className="fas fa-download me-2"></i>Download Sample CSV</button>
                  <button className="btn btn-outline-secondary" onClick={() => fileInputRef.current?.click()}><i className="fas fa-upload me-2"></i>Select CSV File</button>
                  <input type="file" ref={fileInputRef} className="d-none" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
                </div>
                {importFile && (
                  <div className="border rounded p-3 mb-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <i className="fas fa-file-csv text-success me-2"></i>
                        <span className="fw-semibold">{importFile.name}</span>
                        <small className="text-muted d-block">{(importFile.size / 1024).toFixed(1)} KB</small>
                      </div>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => { setImportFile(null); setImportPreview([]); }}><i className="fas fa-times"></i> Remove</button>
                    </div>
                  </div>
                )}
                {importPreview.length > 0 && (
                  <div className="mt-3">
                    <h6 className="mb-2">🔍 Preview (First 5 rows)</h6>
                    <div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      <table className="table table-sm table-bordered small">
                        <thead className="table-light"><tr>{importPreview[0]?.map((cell, i) => <th key={i}>{cell}</th>)}</tr></thead>
                        <tbody>{importPreview.slice(1).map((row, i) => (<tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>))}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ✅ NEW: Premium Type Selection for Import */}
                <h6 className="border-bottom pb-2 mb-3 mt-4">💳 Select Premium Type for Import</h6>
                <div className="mb-3">
                  <div className="d-flex gap-3 flex-wrap">
                    <div 
                      className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'minimum' ? 'border-primary bg-light' : ''}`} 
                      style={{cursor: 'pointer', minWidth: '200px'}} 
                      onClick={() => setSelectedPremiumType('minimum')}
                    >
                      <input className="form-check-input" type="radio" name="premiumTypeImport" id="premiumMinimumImport" value="minimum" checked={selectedPremiumType === 'minimum'} onChange={() => setSelectedPremiumType('minimum')} />
                      <label className="form-check-label w-100" htmlFor="premiumMinimumImport">
                        <strong>Minimum Premium</strong>
                        <small className="d-block text-muted">{formatCurrency(product?.minimumPremiumValue || 0)} / month</small>
                      </label>
                    </div>
                    <div 
                      className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'family' ? 'border-primary bg-light' : ''}`} 
                      style={{cursor: 'pointer', minWidth: '200px'}} 
                      onClick={() => setSelectedPremiumType('family')}
                    >
                      <input className="form-check-input" type="radio" name="premiumTypeImport" id="premiumFamilyImport" value="family" checked={selectedPremiumType === 'family'} onChange={() => setSelectedPremiumType('family')} />
                      <label className="form-check-label w-100" htmlFor="premiumFamilyImport">
                        <strong>Family Premium</strong>
                        <small className="d-block text-muted">{formatCurrency(product?.familyPremiumValue || 0)} / month</small>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Period Selection for Import */}
                <h6 className="border-bottom pb-2 mb-3 mt-4">📅 Select Coverage Period for Import</h6>
                <div className="mb-3 period-dropdown">
                  <label className="form-label small fw-semibold">Coverage Period <span className="text-danger">*</span></label>
                  <div className="position-relative">
                    <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ border: `2px solid ${showPeriodDropdown ? '#F89423' : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>{selectedPeriod ? selectedPeriod.label : 'Select payment plan'}</span>
                      <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                    </div>
                    {showPeriodDropdown && (
                      <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>{renderPeriodOptions()}</div>
                    )}
                  </div>
                  {selectedPeriod && renderPremiumInfo()}
                </div>

                {selectedPeriod && pendingStaffData?.staffCount > 0 && (
                  <div className="alert alert-info border-0 mt-3" style={{ backgroundColor: '#f0f9ff' }}>
                    <h6 className="mb-2">💰 Import Premium Summary</h6>
                    <div className="row small">
                      <div className="col-6"><span className="text-muted">Staff Count:</span> <span className="fw-semibold ms-2">{pendingStaffData.staffCount}</span></div>
                      <div className="col-6"><span className="text-muted">Period:</span> <span className="fw-semibold ms-2">{selectedPeriod.label}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Per Staff/Month:</span> <span className="fw-semibold ms-2">₦{activePremium.toLocaleString()}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Total Premium:</span> <span className="fw-bold ms-2 text-success">{formatCurrency(pendingStaffData.staffCount * activePremium * selectedPeriod.months)}</span></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top">
                <button type="button" className="btn btn-secondary" onClick={closeStaffModal}>Cancel</button>
                <button className="btn btn-success" onClick={handleImportProceed} disabled={!importFile || !selectedPeriod || !pendingStaffData?.staffCount}><i className="fas fa-credit-card me-2"></i>Proceed to Payment</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Renewal Modal */}
      {showBulkRenewalModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title">🔄 Bulk Renew HMO Coverage</h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkRenewalModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Renewal Mode Selection */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Select Staff to Renew</label>
                  <div className="btn-group w-100" role="group">
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleRenewalModeChange('all')}>All Staff ({staffList.length})</button>
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'expired' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleRenewalModeChange('expired')}>Expired Only ({staffSummary.expiredCount})</button>
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'expiring' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleRenewalModeChange('expiring')}>Expiring Soon ({staffSummary.expiringSoonCount})</button>
                  </div>
                </div>

                {/* Selected Staff List */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Selected Staff ({renewalSelection.selectedStaffIds.length})</label>
                  <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {renewalSelection.selectedStaffIds.length === 0 ? (
                      <p className="text-muted small mb-0">No staff selected. Choose a filter above or select individually from the table.</p>
                    ) : (
                      <ul className="list-unstyled mb-0 small">
                        {staffList.filter(s => renewalSelection.selectedStaffIds.includes(s.staffId)).slice(0, 10).map(staff => (
                          <li key={staff.staffId} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                            <span>{staff.firstName} {staff.lastName}</span>
                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleStaffSelectionToggle(staff.staffId)}><i className="fas fa-times"></i></button>
                          </li>
                        ))}
                        {renewalSelection.selectedStaffIds.length > 10 && (<li className="text-muted small py-1">+ {renewalSelection.selectedStaffIds.length - 10} more...</li>)}
                      </ul>
                    )}
                  </div>
                </div>

                {/* ✅ NEW: Premium Type Selection for Bulk Renewal */}
                <h6 className="border-bottom pb-2 mb-3 mt-4">💳 Select Premium Type for Renewal</h6>
                <div className="mb-3">
                  <div className="d-flex gap-3 flex-wrap">
                    <div 
                      className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'minimum' ? 'border-primary bg-light' : ''}`} 
                      style={{cursor: 'pointer', minWidth: '200px'}} 
                      onClick={() => setSelectedPremiumType('minimum')}
                    >
                      <input className="form-check-input" type="radio" name="premiumTypeRenewal" id="premiumMinimumRenewal" value="minimum" checked={selectedPremiumType === 'minimum'} onChange={() => setSelectedPremiumType('minimum')} />
                      <label className="form-check-label w-100" htmlFor="premiumMinimumRenewal">
                        <strong>Minimum Premium</strong>
                        <small className="d-block text-muted">{formatCurrency(product?.minimumPremiumValue || 0)} / month</small>
                      </label>
                    </div>
                    <div 
                      className={`form-check flex-fill p-3 border rounded ${selectedPremiumType === 'family' ? 'border-primary bg-light' : ''}`} 
                      style={{cursor: 'pointer', minWidth: '200px'}} 
                      onClick={() => setSelectedPremiumType('family')}
                    >
                      <input className="form-check-input" type="radio" name="premiumTypeRenewal" id="premiumFamilyRenewal" value="family" checked={selectedPremiumType === 'family'} onChange={() => setSelectedPremiumType('family')} />
                      <label className="form-check-label w-100" htmlFor="premiumFamilyRenewal">
                        <strong>Family Premium</strong>
                        <small className="d-block text-muted">{formatCurrency(product?.familyPremiumValue || 0)} / month</small>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Renewal Period Selection */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Renewal Period <span className="text-danger">*</span></label>
                  <div className="position-relative">
                    <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ border: `2px solid ${showPeriodDropdown ? '#F89423' : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: renewalSelection.renewalPeriod ? '#111827' : '#6b7280' }}>{renewalSelection.renewalPeriod ? renewalSelection.renewalPeriod.label : 'Select renewal period'}</span>
                      <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                    </div>
                    {showPeriodDropdown && (
                      <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                        {PAYMENT_PERIODS.map((period) => (
                          <div key={period.value} onClick={() => handleRenewalPeriodSelect(period)} className="p-3 period-option" style={{ backgroundColor: renewalSelection.renewalPeriod?.value === period.value ? '#FFF8F0' : 'white', color: renewalSelection.renewalPeriod?.value === period.value ? '#F89423' : '#111827', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>{period.label} - {formatCurrency(activePremium * period.months)} per staff</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium Summary */}
                {renewalSelection.renewalPeriod && renewalSelection.selectedStaffIds.length > 0 && (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f0f9ff' }}>
                    <h6 className="mb-2">💰 Renewal Premium Summary</h6>
                    <div className="row small">
                      <div className="col-6"><span className="text-muted">Staff Count:</span> <span className="fw-semibold ms-2">{renewalSelection.selectedStaffIds.length}</span></div>
                      <div className="col-6"><span className="text-muted">Period:</span> <span className="fw-semibold ms-2">{renewalSelection.renewalPeriod.label}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Per Staff/Month:</span> <span className="fw-semibold ms-2">₦{activePremium.toLocaleString()}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Total Premium:</span> <span className="fw-bold ms-2 text-success">{formatCurrency(renewalSelection.renewalPremium)}</span></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkRenewalModal(false)}>Cancel</button>
                <button className="btn btn-warning" onClick={prepareBulkRenewalPayment} disabled={renewalSelection.selectedStaffIds.length === 0 || !renewalSelection.renewalPeriod || !paystackLoaded}>
                  <i className="fas fa-credit-card me-2"></i>Proceed to Payment ({formatCurrency(renewalSelection.renewalPremium)})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .product-details-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .hospitals-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; padding: 12px 0; }
        .hospital-card { background: #f8f9fa; border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; transition: transform 0.2s, box-shadow 0.2s; }
        .hospital-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-color: #F89423; }
        .hospital-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
        .hospital-header h6 { font-weight: 600; color: #1f2937; margin: 0; }
        .hospital-header i { color: #F89423; font-size: 18px; }
        .hospital-location, .hospital-address, .hospital-contact { margin: 4px 0; color: #4b5563; display: flex; align-items: flex-start; gap: 6px; }
        .hospital-location i, .hospital-address i, .hospital-contact i { color: #F89423; margin-top: 3px; font-size: 12px; }
        .text-orange { color: #F89423 !important; }
        .badge.bg-purple { background-color: #6f42c1 !important; }
        .modal.fade.show { display: block; }
        .modal-dialog-scrollable { max-height: calc(100vh - 3.5rem); }
        .modal-body { max-height: calc(100vh - 12rem); overflow-y: auto; }
        .period-dropdown .position-absolute { box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; }
        .period-dropdown .p-3:hover { background-color: #FFF8F0 !important; }
        .period-option:hover { background-color: #FFF8F0 !important; }
        .coverage-card { transition: transform 0.2s ease, box-shadow 0.2s ease; min-width: 200px; }
        .coverage-card:hover { transform: translateY(-2px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .table-warning { background-color: #fff3cd !important; }
        .table-danger { background-color: #f8d7da !important; }
        .btn-group .btn { border-radius: 4px !important; }
        .btn-group .btn:not(:last-child) { border-top-right-radius: 0 !important; border-bottom-right-radius: 0 !important; }
        .btn-group .btn:not(:first-child) { border-top-left-radius: 0 !important; border-bottom-left-radius: 0 !important; margin-left: -1px; }
        .btn-outline-info:hover { background-color: #0dcaf0; border-color: #0dcaf0; color: #000; }
        /* Avatar Upload Styles */
        .avatar-wrapper { position: relative; display: inline-block; }
        .avatar-wrapper:hover .avatar-upload-btn { display: flex !important; }
        .avatar-upload-btn:hover { transform: scale(1.1); box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important; }
        .bg-gradient-primary { background: linear-gradient(135deg, #F89423 0%, #e67e00 100%) !important; }
        .avatar-wrapper .bg-gradient-primary { display: flex !important; }
        .avatar-wrapper img + .bg-gradient-primary { display: none !important; }
        @media (max-width: 992px) {
          .main-content { margin-left: 0; }
        }
      `}</style>
    </div>
  );
}

export default ProductDetails;